import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { api, errorMessage } from "../api";
import type { Item, ItemDetail, Meeting } from "../api/types";
import { Avatar, DueChip, ErrorBox, PriorityMarker, Spinner, SyncBadges } from "../components/bits";
import { MenuButton, useContextMenu } from "../components/ContextMenu";
import { DeleteItemDialog } from "../components/DeleteItemDialog";
import { MarkdownEditor } from "../editor/MarkdownEditor";
import { formatDateTime, formatTime } from "../lib/format";
import { loadCollection, patchItem, store, toastError, upsertItems } from "../store/app";
import { useActorMap, useActors, useAllCollections, useApp, useItem, useMyActorId } from "../store/hooks";
import { sortedStatuses } from "./CollectionPage";
import { rememberTaskCollection, TaskCollectionSelect } from "./MeetingsPage";
import { toggleDone } from "./ListView";

function ActionItems({ meetingId, relations, onRefresh }: { meetingId: string; relations: ItemDetail["relations"]; onRefresh: () => void }) {
  const items = useApp((s) => s.items);
  const collections = useAllCollections();
  const details = useApp((s) => s.details);
  const actors = useActorMap();
  const location = useLocation();
  const actionItems: Item[] = relations
    .filter((r) => r.type === "created_from" && r.item.id !== meetingId)
    .map((r) => items[r.item.id] ?? r.item);
  const colKey = [...new Set(actionItems.map((i) => i.collection_id))].join(",");
  useEffect(() => {
    for (const c of colKey.split(",").filter(Boolean)) if (!store.get().details[c]) void loadCollection(c).catch(() => undefined);
  }, [colKey]);

  return (
    <aside className="action-items" aria-label="Action items">
      <div className="action-items-head">
        <h2>Action items</h2>
        <button type="button" className="btn-link small" onClick={onRefresh}>
          Refresh
        </button>
      </div>
      {actionItems.length === 0 ? (
        <p className="muted small">
          Write task lines in the notes, e.g. <code>- [ ] Send minutes @alice due:fri</code>. They become real tasks and
          appear here.
        </p>
      ) : (
        <ul className="rows compact">
          {actionItems.map((it) => (
            <li key={it.id} className={`row${it.status_category === "done" ? " done" : ""}`}>
              <input
                type="checkbox"
                className="row-check"
                checked={it.status_category === "done"}
                aria-label={`Toggle ${it.title}`}
                onChange={() => toggleDone(it, sortedStatuses(details[it.collection_id]))}
              />
              <PriorityMarker priority={it.priority} />
              <Link to={`/items/${it.id}`} state={{ background: location }} className="rel-link">
                {it.title || "(untitled)"}
              </Link>
              <span className="row-meta">
                <Link to={`/c/${it.collection_id}`} className="chip chip-col" title="Open collection">
                  {collections.find((c) => c.id === it.collection_id)?.name ?? "Collection"}
                </Link>
                <span className="chip chip-status">{it.status_category.replace("_", " ")}</span>
                <DueChip item={it} />
                {it.assignees.slice(0, 2).map((a) => (
                  <Avatar key={a} actor={actors.get(a)} size={18} />
                ))}
                <SyncBadges sync={it.sync} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function MeetingPage() {
  const { id = "" } = useParams();
  const [detail, setDetail] = useState<ItemDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingMeta, setEditingMeta] = useState(false);
  const storeItem = useItem(id);
  const item = storeItem ?? detail?.item;
  const actors = useActors();
  const actorMap = useActorMap();
  const collections = useAllCollections();
  const me = useApp((s) => s.me);
  const myActorId = useMyActorId();
  const allItems = useApp((s) => s.items);
  const refreshTimer = useRef<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const headerMenu = useContextMenu(
    () => [
      { label: "Edit details", icon: "edit", onSelect: () => setEditingMeta(true) },
      { separator: true },
      { label: "Delete meeting…", icon: "trash", danger: true, onSelect: () => setDeleting(true) },
    ],
    "Meeting actions",
  );

  const fetchDetail = () =>
    api
      .item(id)
      .then((d) => {
        setDetail(d);
        setError(null);
        upsertItems([d.item, ...d.relations.map((r) => r.item)]);
      })
      .catch((e) => setError(errorMessage(e)));

  useEffect(() => {
    setDetail(null);
    void fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // When items change (task lines processed server-side → item.changed events), refresh relations.
  useEffect(() => {
    if (!detail) return;
    if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
    refreshTimer.current = window.setTimeout(() => void fetchDetail(), 2000);
    return () => {
      if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems]);

  const labels = useMemo(() => [...new Set(Object.values(allItems).flatMap((i) => i.labels))].sort(), [allItems]);

  if (error && !detail) return <div className="page"><ErrorBox error={error} onRetry={fetchDetail} /></div>;
  if (!detail || !item) return <Spinner />;
  const meeting: Meeting | null = detail.meeting;

  const saveMeeting = async (patch: Partial<Omit<Meeting, "item_id">>) => {
    try {
      const updated = await api.patchItem(item.id, {
        meeting: patch,
        ...(patch.starts_at !== undefined ? { start_at: patch.starts_at } : {}),
      });
      upsertItems([updated]);
      await fetchDetail();
    } catch (e) {
      toastError(e, "Could not update meeting");
    }
  };

  return (
    <div className="page meeting-page">
      <header className="meeting-head">
        <div className="crumbs muted small">
          <Link to="/meetings">Meetings</Link>
          <span>/</span>
          <span>{collections.find((c) => c.id === item.collection_id)?.name}</span>
        </div>
        <div className="meeting-title-row">
          <h1>{item.title}</h1>
          <MenuButton label="Meeting actions" expanded={headerMenu.isOpen} onOpen={headerMenu.openFrom} />
          {headerMenu.menu}
        </div>
        {!editingMeta ? (
          <div className="meeting-meta">
            <span>
              {meeting?.starts_at ? formatDateTime(meeting.starts_at) : "No time set"}
              {meeting?.ends_at && ` – ${formatTime(meeting.ends_at)}`}
            </span>
            {meeting?.location && <span className="muted">at {meeting.location}</span>}
            {meeting?.online_url && (
              <a href={meeting.online_url} target="_blank" rel="noreferrer noopener">
                Join online
              </a>
            )}
            <button type="button" className="btn-link small" onClick={() => setEditingMeta(true)}>
              Edit
            </button>
            <span className="task-target chip" title="Collection where task lines from these notes are created">
              <span>Tasks →</span>
              <TaskCollectionSelect
                className="task-target-select"
                ariaLabel="Tasks go to collection"
                emptyLabel="this meeting's collection"
                value={meeting?.task_collection_id ?? ""}
                collections={collections}
                onChange={(cid) => {
                  if (!cid) return;
                  rememberTaskCollection(cid);
                  void saveMeeting({ task_collection_id: cid });
                }}
              />
              {meeting?.task_collection_id && (
                <Link
                  to={`/c/${meeting.task_collection_id}`}
                  className="task-target-link"
                  aria-label="Open task collection"
                  title="Open collection"
                >
                  ↗
                </Link>
              )}
            </span>
          </div>
        ) : (
          <form
            className="meeting-meta-form"
            onSubmit={async (e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              const s = String(f.get("starts") || "");
              const en = String(f.get("ends") || "");
              await saveMeeting({
                starts_at: s ? new Date(s).toISOString() : null,
                ends_at: en ? new Date(en).toISOString() : null,
                location: String(f.get("location") || "") || null,
                online_url: String(f.get("online_url") || "") || null,
              });
              setEditingMeta(false);
            }}
          >
            <label className="field">
              <span>Starts</span>
              <input type="datetime-local" name="starts" defaultValue={toLocalInput(meeting?.starts_at ?? null)} />
            </label>
            <label className="field">
              <span>Ends</span>
              <input type="datetime-local" name="ends" defaultValue={toLocalInput(meeting?.ends_at ?? null)} />
            </label>
            <label className="field">
              <span>Location</span>
              <input name="location" defaultValue={meeting?.location ?? ""} />
            </label>
            <label className="field">
              <span>Online URL</span>
              <input name="online_url" type="url" defaultValue={meeting?.online_url ?? ""} />
            </label>
            <div className="form-actions">
              <button type="button" className="btn small" onClick={() => setEditingMeta(false)}>
                Cancel
              </button>
              <button type="submit" className="btn small primary">
                Save
              </button>
            </div>
          </form>
        )}
        <div className="attendees" aria-label="Attendees">
          <span className="muted small">Attendees:</span>
          {item.assignees.map((a) => (
            <span key={a} className="chip chip-person removable">
              <Avatar actor={actorMap.get(a)} size={18} />
              {actorMap.get(a)?.display_name ?? "Unknown"}
              <button
                type="button"
                aria-label={`Remove ${actorMap.get(a)?.display_name ?? "attendee"}`}
                onClick={() =>
                  patchItem(item.id, { assignees: item.assignees.filter((x) => x !== a) }).catch((e) =>
                    toastError(e, "Update failed"),
                  )
                }
              >
                ×
              </button>
            </span>
          ))}
          <select
            aria-label="Add attendee"
            value=""
            onChange={(e) =>
              e.target.value &&
              patchItem(item.id, { assignees: [...item.assignees, e.target.value] }).catch((err) =>
                toastError(err, "Update failed"),
              )
            }
          >
            <option value="">+ Add attendee</option>
            {actors
              .filter((a) => !item.assignees.includes(a.id))
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.display_name}
                </option>
              ))}
          </select>
        </div>
      </header>

      <div className="meeting-body">
        <section className="meeting-notes" aria-label="Notes">
          <MarkdownEditor
            key={item.body_doc_id ?? "detached"}
            docId={item.body_doc_id}
            initialMarkdown={detail.body}
            onDetachedSave={async (md) => {
              const updated = await api.patchItem(item.id, { body: md });
              upsertItems([updated]);
              if (updated.body_doc_id && !item.body_doc_id) void fetchDetail();
            }}
            ariaLabel="Meeting notes"
            placeholder="Meeting notes… use - [ ] for action items"
            user={me ? { name: actors.find((a) => a.id === myActorId)?.display_name ?? me.account.email } : undefined}
            context={{
              members: actors,
              labels,
              collections: collections.filter((c) => c.space_id === item.space_id).map((c) => c.name),
            }}
          />
        </section>
        <ActionItems meetingId={item.id} relations={detail.relations} onRefresh={() => void fetchDetail()} />
      </div>
      {deleting && (
        <DeleteItemDialog item={item} onClose={() => setDeleting(false)} onDeleted={() => navigate("/meetings")} />
      )}
    </div>
  );
}
